import axios from 'axios';
import {apolloPlatformAxiosInstance as axiosInstance} from './apolloPlatformAxiosInstance';
import appConfig from '@/config';
import type {SearchCommand} from '@/composables/useSearchCommands';
import {PaginatedSpherePage} from '@/stores/apolloPlatform/cores/portalNavigation';

/**
 * CMS API
 */

export const apiGetPreviewData = (token: string, content_type: string) => {
  const url = `${appConfig.BACKEND_API}cms/page_preview/`;
  return axios.get(url, {
    params: {token, content_type},
  });
};

export const apiGetCmsPages = (params?: {
  locale?: string;
  slug?: string;
  translation_of?: number;
}): Promise<AxiosResponse<PaginatedSpherePage>> => {
  return axiosInstance.get('cms/pages/', {params});
};

export const apiGetCmsPage = (
  detail_url: string,
  locale?: 'en' | 'nl',
): Promise<AxiosResponse<SpherePage<SpherePageTypePage>>> => {
  return axiosInstance.get(detail_url, {params: {locale}});
};

export const searchCmsPages = (
  query: string,
): Promise<
  AxiosResponse<
    PaginatedSpherePage & {
      commands?: SearchCommand[];
      /** Pages editors promoted for this search (Wagtail promoted search results) */
      promoted?: SpherePage<SpherePageType>[];
    }
  >
> => {
  return axiosInstance.get('cms/pages/', {params: {search: query.trim().slice(0, 255)}});
};

export type RecentSearch = {query: string; timestamp: string};

/**
 * The user's most recent distinct searches, newest first.
 */
export const apiGetRecentSearches = (limit = 10): Promise<AxiosResponse<RecentSearch[]>> => {
  return axiosInstance.get('search/recent', {params: {limit}});
};

/**
 * Forget all of the user's recent searches.
 */
export const apiClearRecentSearches = (): Promise<AxiosResponse<void>> => {
  return axiosInstance.delete('search/recent');
};

/** What the search endpoints return of a page: enough to navigate to it. */
export type SearchPage = Pick<
  SpherePageMeta<SpherePageType>,
  'id' | 'title' | 'type' | 'slug' | 'icon' | 'locale'
>;

export type PromotedSearch = {keyword: string; page: SearchPage};

export type RecentPage = {page: SearchPage; timestamp: string};

/**
 * The pages the user visited most recently: first for the selected customer, then for any customer.
 */
export const apiGetRecentPages = (limit = 10): Promise<AxiosResponse<RecentPage[]>> => {
  return axiosInstance.get('search/recent/pages', {params: {limit}});
};

/**
 * The promoted searches (keyword -> page) the user may see.
 */
export const apiGetPromotedSearches = (): Promise<AxiosResponse<PromotedSearch[]>> => {
  return axiosInstance.get('search/promoted');
};

export const apiGetCmsDashboard = (
  dashboardId?: number,
): Promise<AxiosResponse<PaginatedSphereWidget>> => {
  return axiosInstance.get('cms/dashboard/', {
    params: dashboardId ? {child_of: dashboardId} : undefined,
  });
};
